<template>
  <div>
    <el-dialog
      v-model="visible"
      class="progress-dialog"
      align-center
      :title="dialogTitle"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="true"
      :before-close="handleBeforeClose"
      width="509px"
      @closed="handleClosed"
    >
      <div class="progress-dialog-body">
        <!-- Circular Progress Ring -->
        <div class="progress-ring-wrapper">
          <el-progress
            type="circle"
            :percentage="percentage"
            :stroke-width="4"
            :width="60"
            :color="progressColor"
          />
        </div>

        <!-- Progress Text -->
        <div class="progress-message-text">
          {{ displayMessage || `正在${dialogTitle}中....${percentage}%` }}
        </div>

        <!-- Subtitle -->
        <div class="progress-subtitle-text">请稍等片刻，勿刷新或关闭网页</div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
  import { ElDialog, ElProgress } from 'element-plus';
  import { nextTick, onMounted, ref } from 'vue';

  interface Props {
    title?: string;
    canClose?: boolean;
    cancel?: () => void;
    onDestroy: () => void;
  }

  const props = withDefaults(defineProps<Props>(), { title: '进行中', canClose: false });

  // 初始隐藏，等 DOM 布局完成后再打开。
  // 否则 el-progress SVG 的 stroke-dasharray 初始值未被浏览器"确认"，
  // 后续 update() 改变百分比时 CSS transition 无起始状态，动画不生效。
  const visible = ref(false);
  onMounted(() => {
    nextTick(() => {
      visible.value = true;
    });
  });

  const dialogTitle = ref(props.title);
  const percentage = ref(0);
  const displayMessage = ref('');

  // el-progress 环形进度颜色
  const progressColor = '#6324eb';

  // Exposed: update percentage / message / title
  const update = (p?: number, msg?: string, titleText?: string) => {
    if (p !== undefined) {
      percentage.value = Math.max(0, Math.min(100, p));
    }
    if (msg !== undefined) {
      displayMessage.value = msg;
    }
    if (titleText) {
      dialogTitle.value = titleText;
    }
    if (!dialogTitle.value) {
      dialogTitle.value = '进行中';
    }
  };

  const handleBeforeClose = (done: () => void) => {
    if (props.canClose) {
      done();
    }
    // canClose 为 false 时阻止关闭，保持弹窗显示
  };

  const close = () => {
    visible.value = false;
  };

  const handleClosed = () => {
    props.onDestroy();
  };

  defineExpose({ update, close });
</script>

<style scoped>
  /* el-dialog panel - match Figma card style */
  .progress-dialog :deep(.el-dialog) {
    width: 509px;
    background: linear-gradient(180deg, #f6f9f7 0%, #f6f9f7 100%);
    border-radius: 12px;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.15);
  }

  /* Header */
  .progress-dialog :deep(.el-dialog__header) {
    padding: 32px 32px 0 32px;
    margin: 0;
  }

  .progress-dialog :deep(.el-dialog__title) {
    font-family: Inter, sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    color: #333940;
  }

  .progress-dialog :deep(.el-dialog__headerbtn) {
    top: 32px;
    right: 32px;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
  }

  .progress-dialog :deep(.el-dialog__headerbtn .el-dialog__close) {
    color: #999999;
    font-size: 12px;
  }

  .progress-dialog :deep(.el-dialog__headerbtn:hover) {
    background: rgba(0, 0, 0, 0.06);
  }

  /* Body */
  .progress-dialog :deep(.el-dialog__body) {
    padding: 0 32px !important;
  }

  .progress-dialog-body {
    height: 400px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  /* Circular ring */
  .progress-ring-wrapper {
    margin-bottom: 26px;
  }

  .progress-ring-wrapper :deep(.el-progress-circle__track) {
    stroke: #e8e8e8;
  }

  /* Progress text */
  .progress-message-text {
    margin-bottom: 10px;
    font-family: 'Source Han Sans CN VF', Inter, sans-serif;
    font-size: 18px;
    font-weight: 400;
    line-height: 26px;
    color: #160211;
    text-align: center;
  }

  /* Subtitle */
  .progress-subtitle-text {
    font-family: 'Source Han Sans CN VF', Inter, sans-serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 22px;
    color: #6e6a7c;
    text-align: center;
  }
</style>
